import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect } from 'react';
import { Form, Skeleton } from 'antd';

import { Helmet } from 'react-helmet';
import { PageProps } from '@/types/PageProps';

import { SaveButton, TabContentWrapper } from '@/components/_common';

import { useRouteMatchParam } from '@/hooks';
import { usePurchase } from '@/core/domain/purchase/Purchase.hooks';
import PurchaseItemsFormTabs from '@/components/Movements/Purchases/PurchaseDetails/PurchaseItemsPurchase/PurchaseItemsFormTabs';
import {
  PurchaseItem,
  PurchaseItemsFormValues,
  UpdatePurchaseItemRequest,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import {
  useCalcTaxesItemPurchases,
  usePurchaseItem,
  usePurchaseItemUpdate,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { useHistory } from 'react-router-dom';
import { PurchaseItemType } from '@/core/domain/purchase/Purchase.types';
import PurchaseItemsButtonsRepeated from '@/components/Movements/Purchases/PurchaseDetails/PurchaseItemsPurchase/PurchaseItemsButtonsRepeated';
import { useRequestPurchaseDetail } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import moment from 'moment';

function PurchaseDetailItemPurchase({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const [form] = Form.useForm<PurchaseItemsFormValues>();

  const itemId = Number(useRouteMatchParam('itemId'));
  const purchaseId = +useRouteMatchParam('purchaseId');

  const { data: item, isLoading } = usePurchaseItem(itemId);

  const updateItemMutation = usePurchaseItemUpdate();
  const calcTaxesMutation = useCalcTaxesItemPurchases();

  const purchaseDetail = useRequestPurchaseDetail(
    item?.workOrderPurchaseQuotationsId,
  );

  useEffect(() => {
    if (item) {
      form.setFieldsValue({
        ...item,
        cfopId: item?.cfop?.id,
        ncmId: item?.ncm?.id,
        purchaseItemTaxes: {
          ...item.purchaseItemTaxes,
          competenceApuration: item.purchaseItemTaxes?.competenceApuration
            ? moment(item.purchaseItemTaxes.competenceApuration)
            : undefined,
          ibsZfmAssessmentPeriod: item.purchaseItemTaxes?.ibsZfmAssessmentPeriod
            ? moment(item.purchaseItemTaxes.ibsZfmAssessmentPeriod)
            : undefined,
          calcPercentPis:
            item.purchaseItemTaxes?.calcPercentPis === false ? 0 : 1,
          calcPercentPisSt:
            item.purchaseItemTaxes?.calcPercentPisSt === false ? 0 : 1,
          calcPercentCofins:
            item.purchaseItemTaxes?.calcPercentCofins === false ? 0 : 1,
          calcPercentCofinsSt:
            item.purchaseItemTaxes?.calcPercentCofinsSt === false ? 0 : 1,
        },
      });
    }
  }, [item, updateItemMutation?.data]);

  const { data: purchase } = usePurchase(purchaseId);

  const parseBool = (
    formValue?: number | boolean | undefined,
    itemValue?: boolean | undefined,
  ) => {
    if (typeof formValue === 'number') {
      return formValue !== undefined ? formValue === 1 : itemValue;
    }

    if (typeof formValue === 'boolean') {
      return formValue;
    }

    return itemValue;
  };

  const handleUpdateItemPurchase = async (
    stayPage?: boolean,
    response?: PurchaseItem,
  ) => {
    try {
      const values = await form.validateFields();
      const purchaseItemTaxesData = item?.purchaseItemTaxes;

      const obj: UpdatePurchaseItemRequest = {
        ...values,
        amountReturned: item?.amountReturned ?? undefined,
        purchase: purchaseId,
        id: itemId,
        partId: item?.partId,
        consumableId: item?.consumableId,
        type: item?.partId
          ? PurchaseItemType.part
          : item?.consumableId
          ? PurchaseItemType.consumable
          : PurchaseItemType.task,
        purchaseItemTaxes: {
          ...purchaseItemTaxesData,
          ...values.purchaseItemTaxes,
          ...response?.purchaseItemTaxes,
          competenceApuration:
            values.purchaseItemTaxes?.competenceApuration?.format('YYYY-MM-DD'),
          ibsZfmAssessmentPeriod:
            values.purchaseItemTaxes?.ibsZfmAssessmentPeriod?.format(
              'YYYY-MM-DD',
            ),
          calcPercentPis: parseBool(
            values.purchaseItemTaxes?.calcPercentPis as number,
            item?.purchaseItemTaxes?.calcPercentPis as boolean,
          ),
          calcPercentPisSt: parseBool(
            values.purchaseItemTaxes?.calcPercentPisSt as number,
            item?.purchaseItemTaxes?.calcPercentPisSt as boolean,
          ),
          calcPercentCofins: parseBool(
            values.purchaseItemTaxes?.calcPercentCofins as number,
            item?.purchaseItemTaxes?.calcPercentCofins as boolean,
          ),
          calcPercentCofinsSt: parseBool(
            values.purchaseItemTaxes?.calcPercentCofinsSt as number,
            item?.purchaseItemTaxes?.calcPercentCofinsSt as boolean,
          ),
        },
      };

      await updateItemMutation.mutateAsync({
        ...obj,
        noNotification: stayPage,
      });

      if (!stayPage) {
        history.push(
          `/app/movements/purchases/purchasesReleases/${purchaseId}/itemsPurchase`,
        );
      }
    } catch {
      await purchaseDetail.refetch();
      await form.validateFields();
    }
  };

  const handleCalcTaxesItem = async () => {
    const values = await form.validateFields();

    if (item) {
      const obj: UpdatePurchaseItemRequest = {
        ...item,
        id: itemId,
        purchase: purchaseId,
        purchaseItemTaxes: {
          ...item.purchaseItemTaxes,
          ...values.purchaseItemTaxes,
          competenceApuration:
            values.purchaseItemTaxes.competenceApuration?.format(),
          ibsZfmAssessmentPeriod:
            values.purchaseItemTaxes.ibsZfmAssessmentPeriod?.format(),
          calcPercentPis: parseBool(
            values.purchaseItemTaxes?.calcPercentPis as number,
            item.purchaseItemTaxes?.calcPercentPis as boolean,
          ),
          calcPercentPisSt: parseBool(
            values.purchaseItemTaxes?.calcPercentPisSt as number,
            item.purchaseItemTaxes?.calcPercentPisSt as boolean,
          ),
          calcPercentCofins: parseBool(
            values.purchaseItemTaxes?.calcPercentCofins as number,
            item.purchaseItemTaxes?.calcPercentCofins as boolean,
          ),
          calcPercentCofinsSt: parseBool(
            values.purchaseItemTaxes?.calcPercentCofinsSt as number,
            item.purchaseItemTaxes?.calcPercentCofinsSt as boolean,
          ),
        },
      };

      await calcTaxesMutation.mutateAsync(obj).then((response) => {
        form.setFieldsValue(response);
        handleUpdateItemPurchase(true, response);
      });
    }
  };

  return (
    <TabContentWrapper
      permissions={route.permissions}
      title={route.name}
      extra={
        <>
          <SaveButton
            block
            type="primary"
            onClick={() => handleUpdateItemPurchase()}
            loading={
              updateItemMutation.isLoading || purchaseDetail.isRefetching
            }
            disabled={
              purchase?.boschMigration ||
              !hasPermissions([Authorities.MOVIMENTATION_PURCHASE_ITEM_EDIT])
            }
          >
            <FormattedMessage id="commons.entity.save" />
          </SaveButton>
        </>
      }
    >
      <Helmet
        title={intl.formatMessage({
          id: 'layout.menu.movements.purchases.details.itemsPurchase.details',
        })}
      />

      <Skeleton loading={isLoading && purchaseDetail.isLoading}>
        <PurchaseItemsFormTabs
          form={form}
          purchase={purchase}
          isTask={item?.type === PurchaseItemType.task}
          requestPurchase={purchaseDetail.data}
          item={item}
        />

        <PurchaseItemsButtonsRepeated
          purchase={purchase}
          itemId={itemId}
          calcTaxesCallback={handleCalcTaxesItem}
          isLoadingCalcTaxes={calcTaxesMutation.isLoading}
        />
      </Skeleton>
    </TabContentWrapper>
  );
}

export default PurchaseDetailItemPurchase;
